import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  hasOpenOverlay,
  logOverlayDebug,
  releaseStaleOverlays,
  scheduleStaleOverlayRelease,
} from "@/lib/overlay-guard";
import { onAppResume, startAppResumeBus } from "@/lib/app-resume";

/**
 * Mounted once at the app root. Releases stale Radix scroll-locks after:
 *  - every route change (menu item navigation unmounts the overlay mid-close)
 *  - returning from background (iOS WKWebView freezes the JS context)
 *  - page show from bfcache
 *
 * Plus two self-healing layers added for the iOS "hamburger stops responding"
 * regression:
 *  - a capture-phase pointer/touch listener that detects a tap arriving while
 *    the document is locked and releases the lock in the same gesture
 *  - a cheap periodic watchdog for locks that leak without any user input
 *
 * No visual output, no navigation side effects.
 */

function isDocumentLocked(): boolean {
  if (typeof document === "undefined") return false;
  const body = document.body;
  if (!body) return false;
  const root = document.getElementById("root");
  const candidates = [document.documentElement, body, root].filter(Boolean) as HTMLElement[];

  if (body.hasAttribute("data-scroll-locked")) return true;
  if (document.getElementById("native-splash")) return true;

  return candidates.some((element) => {
    const inline = element.style.pointerEvents;
    if (inline === "none") return true;
    if (element.getAttribute("aria-hidden") === "true") return true;
    if (element.hasAttribute("inert")) return true;
    return false;
  });
}

export const OverlayGuard = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Run after the current paint so Radix has a chance to clean up itself.
    return scheduleStaleOverlayRelease(`route:${pathname}`);
  }, [pathname]);

  // Unified resume bus (visibilitychange + focus + pageshow + Capacitor
  // appStateChange, coalesced) — replaces the three separate listeners that
  // used to fire redundant cleanups on every foreground.
  useEffect(() => {
    const stopBus = startAppResumeBus();
    const unsubscribe = onAppResume((detail) => {
      logOverlayDebug(`app-resume:${detail.source}`, undefined, { hiddenMs: detail.hiddenMs });
      // Immediate release so the very first tap after resume lands, then a
      // couple of retries for overlays still finishing their unmount.
      releaseStaleOverlays(`app-resume:${detail.source}`);
      scheduleStaleOverlayRelease(`app-resume:${detail.source}`, [80, 350, 1200]);
    });
    return () => {
      unsubscribe();
      stopBus();
    };
  }, []);

  // Self-heal on the failing gesture itself: if a tap arrives while the
  // document is locked and nothing is actually open, unlock right away.
  useEffect(() => {
    const onGesture = (event: Event) => {
      if (hasOpenOverlay()) return;
      if (!isDocumentLocked()) return;
      logOverlayDebug("locked-gesture-detected", event);
      const released = releaseStaleOverlays("locked-gesture");
      if (released) scheduleStaleOverlayRelease("locked-gesture-followup", [0, 120]);
    };

    document.addEventListener("pointerdown", onGesture, true);
    document.addEventListener("touchstart", onGesture, true);
    return () => {
      document.removeEventListener("pointerdown", onGesture, true);
      document.removeEventListener("touchstart", onGesture, true);
    };
  }, []);

  // Periodic watchdog — cheap attribute checks, only acts when locked.
  useEffect(() => {
    const interval = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      if (hasOpenOverlay()) return;
      if (!isDocumentLocked()) return;
      logOverlayDebug("watchdog-locked");
      releaseStaleOverlays("watchdog");
    }, 3000);
    return () => window.clearInterval(interval);
  }, []);

  return null;
};
